import { differenceInSeconds } from 'date-fns'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LuBolt, LuTriangleAlert } from 'react-icons/lu'

import {
  GEO_FEATURE_MARKER_COLOR,
  HIGH_RISK_MARKER_COLOR,
  useGeoFences,
} from '@/feature/observatory'
import { useGeoFenceEvents } from '@/feature/observatory/api/geoFenceEvents'

import { MarkerBadge } from '@/components/MarkerBadge'

import { useAssetLocationHistory } from '../../api/useAssetLocationHistory'
import { ReplayTimelineProps } from './ReplayTimeline.types'
import { basicAssetTrackMarkers, basicGeoFenceTrackMarkers } from './ReplayTimeline.utils'

const STEP_SIZE_DEBOUNCE_MS = 300

type UseReplayTimelineArgs = Pick<
  ReplayTimelineProps,
  'asset' | 'rangeStartTime' | 'stepSize' | 'setStepSize' | 'history'
>

export const useReplayTimeline = ({
  asset,
  rangeStartTime,
  stepSize,
  setStepSize,
  history,
}: UseReplayTimelineArgs) => {
  // The slider tracks its own value so dragging stays responsive, and only the settled value is
  // pushed outwards — every change refetches the history at a new resolution.
  const setStepSizeDebounced = useCallback(debounce(setStepSize, STEP_SIZE_DEBOUNCE_MS), [
    setStepSize,
  ])
  const [stepValue, setStepValue] = useState(stepSize)

  useEffect(() => {
    setStepSizeDebounced(stepValue)
  }, [stepValue, setStepSizeDebounced])

  const periodInSec = useMemo(
    () => differenceInSeconds(new Date(), rangeStartTime, { roundingMethod: 'ceil' }),
    [rangeStartTime],
  )

  const { data: fetchedHistory } = useAssetLocationHistory(
    history ? null : asset.vehicleBridgeId,
    `${periodInSec}s`,
    `${stepSize}m`,
  )
  const assetHistoryEvents = history ?? fetchedHistory

  // TODO: setting pageSize to 1000 is a hack to make the lookup work which determines
  // whether a geofence is high risk or not (the code silently falls back
  // to classifying it as normal risk if it can't make a match :/)
  //
  // to solve this properly will require collecting the list of ids of geofences
  // involved in the events and fetching limited details about them via a bulk
  // api of sorts
  const { data: geoFences } = useGeoFences({ pageSize: 1000 })
  const { data: geofenceEventsData } = useGeoFenceEvents()

  const markerTracks = useMemo(() => {
    const assetsMarkers = basicAssetTrackMarkers(assetHistoryEvents)
    const [highRisk, normal] = basicGeoFenceTrackMarkers(
      geofenceEventsData,
      geoFences.results,
      asset,
    )

    highRisk.pointChildComponent = () => (
      <MarkerBadge glyph={LuTriangleAlert} color={HIGH_RISK_MARKER_COLOR} size={'1rem'} />
    )

    normal.pointColour = GEO_FEATURE_MARKER_COLOR
    normal.pointChildComponent = () => <LuBolt size="1rem" />

    return [assetsMarkers, highRisk, normal]
  }, [assetHistoryEvents, geoFences.results, asset, geofenceEventsData])

  return { markerTracks, stepValue, setStepValue }
}
